import { PackageTier } from '../types';

export const PACKAGES: PackageTier[] = [
  {
    id: 'starter',
    name: 'AI Starter',
    tagline: 'Pro WordPress / AI Website + Brand Refresh + Foundational SEO & GEO',
    bestFor: 'Small businesses, startups & local service providers',
    popular: false,
    pricing: {
      project: {
        USD: 850,
        KES: 110000,
      },
      waas: {
        setup: {
          USD: 450,
          KES: 58000,
        },
        monthly: {
          USD: 95,
          KES: 12500,
        },
      },
    },
    includedServiceKeys: ['branding', 'websites', 'search', 'hosting'],
    highlights: [
      'Pro WordPress Website Cited by AI (5–7 bespoke pages)',
      'Brand Identity Refresh (20+ yrs hands-on UI/UX & color alignment)',
      'Foundational Google SEO + Schema.org JSON-LD & /llms.txt',
      'Social Media Profile Audit & Bio Optimization (2 channels)',
      'Ad-Ready Meta Pixel & Google Tag Manager (GTM) setup',
      'Instant WhatsApp & Email Lead Routing + Hostinger Cloud SSL',
    ],
    features: {
      branding: [
        'Visual brand alignment & UI/UX styling by 20+ yr Branding Consultant',
        'Typography pairing & WCAG AA accessible color palette tokens',
        'Branded favicon, OpenGraph social share card & email signature',
      ],
      website: [
        '5–7 bespoke Pro WordPress or custom AI-ready conversion pages',
        'Engineered to be cited by AI with clean semantic HTML5',
        'Sub-second load speed (<0.8s Core Web Vitals on mobile & desktop)',
        'Intuitive WordPress CMS editor for effortless team updates',
      ],
      visibility: [
        'Foundational Google Search SEO + Generative Engine Optimization (GEO)',
        'Core Schema.org JSON-LD markup (Organization, LocalBusiness, FAQPage)',
        'Standardized /llms.txt AI crawler discovery file',
        'XML Sitemap, robots.txt & Google Search Console indexing',
      ],
      socialMedia: [
        'Audit & bio/link optimization for 2 core social profiles (e.g., LinkedIn & Instagram)',
        '2 custom-branded social cover banners aligned with website identity',
      ],
      smartAds: [
        'Meta Pixel, Google Analytics 4 (GA4) & Google Tag Manager installation',
        'Conversion event tracking on inquiry forms & WhatsApp clicks',
      ],
      conversion: [
        'High-contrast conversion-engineered CTA placements',
        'Smart interactive inquiry & quote modal with spam protection',
        'Click-to-WhatsApp direct routing button (+254 725 724 175)',
      ],
      automation: [
        'Instant dual-port SMTP email notification on lead submission',
        'Automated branded confirmation email & PDF summary to client',
      ],
      hosting: [
        'Managed Hostinger Cloud LiteSpeed deployment & SSL/TLS 1.3',
        'Custom domain DNS setup & 3 professional business email accounts',
        'Automated weekly cloud backups & firewall protection',
      ],
    },
  },
  {
    id: 'growth',
    name: 'AI Growth',
    tagline: 'Pro WordPress Cited by AI + Full GEO + Social Consulting + 24/7 AI Concierge',
    bestFor: 'Growing companies, e-commerce & competitive service firms',
    popular: true,
    pricing: {
      project: {
        USD: 1650,
        KES: 215000,
      },
      waas: {
        setup: {
          USD: 850,
          KES: 110000,
        },
        monthly: {
          USD: 180,
          KES: 23500,
        },
      },
    },
    includedServiceKeys: ['branding', 'social', 'ads', 'websites', 'search', 'automation', 'hosting'],
    highlights: [
      'Pro WordPress Website Cited by AI (Up to 12 custom pages)',
      'Senior Branding Consultant: Mini Brand Style Guide & Custom UI Kit',
      'Full Generative Engine Optimization (GEO) for ChatGPT & Perplexity',
      'Social Media Consulting: 30-Day Content Playbook & 6 Branded Templates',
      'Digital / Smart Advertisement: 1 High-ROAS Launch Campaign + Landing Page',
      '24/7 AI Sales Concierge + <60s WhatsApp & CRM Lead Automation',
    ],
    features: {
      branding: [
        'Hands-on creative direction from 20+ yr Senior Branding Consultant',
        'Logo refinement, Mini Brand Style Guide (PDF) & visual system',
        'Bespoke UI/UX design kit, custom iconography & branded graphic assets',
      ],
      website: [
        'Up to 12 custom Pro WordPress or full-stack AI-ready pages + Digital Pulse Blog',
        'WooCommerce / online booking / M-Pesa & card payment readiness',
        'Interactive service selector, package comparison & quote estimator',
        'Sub-800ms LiteSpeed Enterprise & Cloudflare CDN edge caching',
      ],
      visibility: [
        'Full Generative Engine Optimization (GEO) for ChatGPT, Perplexity & Google AI',
        'Multi-entity JSON-LD Schema.org graph (Organization, Service, Product, FAQPage)',
        'Complete /llms.txt & /llms-full.txt AI knowledge base endpoints',
        'Citation-optimized FAQ hubs engineered for zero-click AI answers',
      ],
      socialMedia: [
        'Omnichannel Social Media Consulting & positioning audit (4 channels)',
        '30-day editorial content calendar with hooks, pillars & CTAs',
        '6 bespoke social graphic & LinkedIn/Instagram carousel templates',
        'Social selling DM-to-WhatsApp conversion workflow',
      ],
      smartAds: [
        'Setup of 1 Digital / Smart Advertisement campaign (Google Search or Meta Ads)',
        '3 custom-designed high-CTR ad graphics + direct-response ad copy',
        'Message-matched conversion landing page with full pixel attribution',
      ],
      conversion: [
        '24/7 AI Sales Concierge chatbot trained on your services & pricing',
        'Smart lead qualification (budget, timeline & scope scoring)',
        'Direct strategy call booking scheduler & pre-filled WhatsApp handoff',
      ],
      automation: [
        'Sub-60-second lead dispatch via Email, PDF Proposal & WhatsApp',
        'Built-in Lead CRM Pipeline + Newsletter & Bulk Email Campaign Studio',
        'CRM sync with HubSpot, Zoho, Pipedrive, or Google Sheets',
      ],
      hosting: [
        'Managed Hostinger Cloud Business deployment with NVMe SSD storage',
        'Up to 10 corporate business emails with SPF, DKIM, DMARC & MTA-STS',
        'Automated daily cloud backups & 99.9% uptime SLA monitoring',
      ],
    },
  },
  {
    id: 'pro',
    name: 'AI Pro',
    tagline: 'Complete Brand System + Pro WordPress AI Platform + Smart Ads & Full CRM',
    bestFor: 'Established businesses scaling brand authority, paid media & operations',
    popular: false,
    pricing: {
      project: {
        USD: 2850,
        KES: 370000,
      },
      waas: {
        setup: {
          USD: 1450,
          KES: 188000,
        },
        monthly: {
          USD: 290,
          KES: 38000,
        },
      },
    },
    includedServiceKeys: ['branding', 'social', 'ads', 'websites', 'search', 'automation', 'hosting'],
    highlights: [
      'Complete Corporate Brand Identity & Master Brand Book (20+ Yrs Design)',
      'Pro WordPress & Custom AI Web Platform (Up to 20 pages + Case Studies)',
      'Advanced Multi-Entity GEO Authority + Monthly AI Citation Reports',
      'Social Media Consulting: Omnichannel Strategy, 12 Carousels & Team Training',
      'Digital / Smart Ads: Multi-Channel Google + Meta + Retargeting Funnels',
      'Full Autonomous Pipeline: Visitor → AI Concierge → PDF Quote → CRM → Sale',
    ],
    features: {
      branding: [
        'Full Corporate Brand Identity System by 20+ yr Branding Consultant',
        '30+ page Master Brand Guidelines Book (logo, grid, typography, print & web)',
        'Executive stationery suite: business cards, letterhead & capability deck',
      ],
      website: [
        'Up to 20 bespoke Pro WordPress / custom React pages + Case Study & Resource Hub',
        'Advanced WooCommerce store or custom client portal with role-based access',
        'Custom interactive ROI calculators & multi-currency (USD/KES) pricing',
        'Ultra-fast global edge infrastructure with 99.9% uptime SLA',
      ],
      visibility: [
        'Deep entity graph optimization across Google Knowledge Graph & AI engines',
        'Topic-cluster authority articles indexed via Google News & RSS sitemaps',
        '/.well-known/ai-catalog.json Agentic Resource Discovery (ARD) manifest',
        'Monthly AI Search citation tracking & competitor share-of-voice reports',
      ],
      socialMedia: [
        'Full Omnichannel Social Media Consulting (LinkedIn, Instagram, Facebook, X, YouTube)',
        '12 custom-designed carousel systems, infographic templates & video scripts',
        'Executive personal branding advisory + in-house marketing team workshop',
      ],
      smartAds: [
        'Multi-channel Digital / Smart Advertisement (Google Search + Meta + LinkedIn)',
        'Full-funnel 7/14/30-day retargeting architecture & Server-Side CAPI tracking',
        'Dedicated A/B tested campaign landing pages & ROAS attribution dashboard',
      ],
      conversion: [
        'Bespoke trained AI Concierge with deep company knowledge base',
        'Intelligent lead routing based on deal size, industry & urgency',
        'Instant branded PDF quote generation & automated calendar scheduling',
      ],
      automation: [
        'Complete 7-step pipeline: Visitor → AI → Lead → PDF → CRM → WhatsApp → Sale',
        'Two-way sync with HubSpot, Salesforce, Pipedrive, or Zoho CRM',
        'Automated blog-to-newsletter dispatch & segmented bulk email campaigns',
      ],
      hosting: [
        'Dedicated Hostinger Cloud Startup tier with isolated CPU/RAM resources',
        'Unlimited corporate email accounts with full cryptographic deliverability',
        'Daily off-site snapshots, WAF malware shield & priority engineer SLA',
      ],
    },
  },
  {
    id: 'enterprise',
    name: 'AI Enterprise',
    tagline: 'Turnkey Brand Transformation, Custom AI Platform, Smart Media & Dedicated Partner',
    bestFor: 'Large organizations, regional enterprises, NGOs & multi-branch brands',
    popular: false,
    pricing: {
      project: {
        USD: 4900,
        KES: 640000,
      },
      waas: {
        setup: {
          USD: 2600,
          KES: 338000,
        },
        monthly: {
          USD: 550,
          KES: 72000,
        },
      },
    },
    includedServiceKeys: ['branding', 'social', 'ads', 'websites', 'search', 'automation', 'hosting'],
    highlights: [
      ' Dedicated Senior Branding Consultant (20+ Yrs): Full Rebrand & Collateral Suite',
      'Unlimited Pro WordPress / Custom Full-Stack AI Web Platform & Portals',
      'Omnichannel GEO Dominance (ChatGPT, Perplexity, Google SGE, Gemini & Claude)',
      'Turnkey Social Media Governance, Executive Ghostwriting & Video Direction',
      'Managed Digital / Smart Advertisement Command Center & Weekly ROAS Sprints',
      'Custom ERP/CRM/API Integrations + Dedicated Hostinger Cloud Professional Stack',
    ],
    features: {
      branding: [
        'Complete enterprise brand transformation led hands-on (20+ yrs experience)',
        'Master Brand Book, print/packaging/fleet graphics, investor decks & UI design system',
        'Ongoing creative direction for all campaigns, events & corporate publications',
      ],
      website: [
        'Unlimited scalable pages, multi-region portals & custom web applications',
        'Headless Pro WordPress or custom React/Node.js architecture with zero cold-starts',
        'Multi-currency, multi-language & enterprise accessibility (WCAG AAA) compliance',
      ],
      visibility: [
        'Global & regional GEO dominance across all major LLM answer engines',
        'Programmatic Schema.org entity graphs, Wikidata alignment & digital PR citations',
        'Weekly AI brand mention auditing, sentiment defense & editorial publishing',
      ],
      socialMedia: [
        'Turnkey executive thought-leadership & corporate social media governance',
        'Custom motion graphics, carousel production & quarterly team masterclasses',
        'Automated social-to-CRM lead attribution across all regional branches',
      ],
      smartAds: [
        'Dedicated Digital / Smart Advertisement management across Google, Meta, LinkedIn & YouTube',
        'Custom conversion landing page studio for every product line or campaign',
        'Weekly bid optimization, creative refresh sprints & executive ROAS briefings',
      ],
      conversion: [
        'Multi-agent AI ecosystem (Sales Concierge, Customer Support & Onboarding)',
        'Custom interactive client portals, calculators & automated contract workflows',
      ],
      automation: [
        'Custom REST API, webhook, ERP, payment (M-Pesa/Stripe) & CRM synchronization',
        'Enterprise bulk email & newsletter automation with dedicated IP reputation',
        'Quarterly C-suite digital growth roadmaps & priority 24/7 engineering support',
      ],
      hosting: [
        'Hostinger Cloud Professional dedicated cluster with global CDN edge routing',
        'Enterprise DDoS mitigation, staging-to-production CI/CD & real-time uptime telemetry',
      ],
    },
  },
];
